import { useEffect, useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";
import { getData } from "../../Utils/Http/api";

const MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

const AZUL = "info.main";
const NARANJA = "#b45309";

// Alto de todos los cuadros: el del más alto (Gestiones, con título y tres renglones). Así los cinco quedan iguales, estén en una fila o en varias.
const ALTO_CUADRO = 84;

// Cada cuánto se actualiza solo.
const CADA_MS = 5 * 60 * 1000;

// El resumen se guarda fuera del componente: cada pantalla trae su propia barra superior (se vuelve a montar al navegar) y así no se
// vuelve a pedir en cada cambio de pantalla. `oyentes` son las barras montadas, a las que se avisa cuando cambia.
const guardado = { usuario: null, resumen: null, actualizadoEl: 0, ultimoIntentoEl: 0, cargando: false };
const oyentes = new Set();
const avisar = () => oyentes.forEach((oyente) => oyente({ ...guardado }));

const usuarioLogueado = () => localStorage.getItem("iduserlogueado");

// Si en la misma pestaña entra otro usuario, no se le muestra lo del anterior.
const alinearUsuario = () => {
  const usuario = usuarioLogueado();
  if (guardado.usuario === usuario) return;
  Object.assign(guardado, { usuario, resumen: null, actualizadoEl: 0, ultimoIntentoEl: 0, cargando: false });
};

const hayQueActualizar = () => Date.now() - guardado.ultimoIntentoEl >= CADA_MS;

// Pide el resumen del usuario logueado. Si ya hay un pedido en curso no hace otro. Si falla, deja a la vista lo último que se pudo traer.
// La pantalla de Gestión Pre-Judicial la llama al registrar un evento, para que las gestiones de hoy se actualicen enseguida.
export const recargarResumenGestion = async () => {
  alinearUsuario();
  if (guardado.cargando) return;
  guardado.cargando = true;
  avisar();
  try {
    const res = await getData("/MiResumenGestion");
    if (res?.value) {
      guardado.resumen = res.value;
      guardado.actualizadoEl = Date.now();
    }
  } catch (ex) {
    console.error("No se pudo cargar el resumen del gestor:", ex);
  } finally {
    guardado.ultimoIntentoEl = Date.now();
    guardado.cargando = false;
    avisar();
  }
};

const numero = (valor) => (valor ?? 0).toLocaleString("es-AR");
const porcentaje = (parte, total) => (total > 0 ? Math.round((100 * parte) / total) : 0);
const hora = (marca) => new Date(marca).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false });

// «27 OCT» a partir de la fecha que manda el backend (sin zona: se toma tal cual).
const diaCorto = (iso) => {
  const fecha = new Date(iso);
  return Number.isNaN(fecha.getTime()) ? "hoy" : `${fecha.getDate()} ${MESES[fecha.getMonth()]}`;
};

// Un cuadro del banner: título chico en mayúsculas y, debajo, las cifras. El tooltip explica de dónde sale cada una.
function Cuadro({ titulo, explicacion, children }) {
  return (
    <Tooltip title={explicacion} arrow placement="bottom">
      <MDBox
        bgColor="white"
        borderRadius="lg"
        px={1.5}
        py={1}
        sx={{ minWidth: 104, minHeight: ALTO_CUADRO, lineHeight: 1.25, cursor: "default", boxShadow: "0 1px 4px rgba(0, 0, 0, 0.16)" }}
      >
        <MDTypography
          display="block"
          variant="caption"
          fontWeight="bold"
          sx={{ color: "text.secondary", textTransform: "uppercase", fontSize: "0.62rem", letterSpacing: "0.02em", lineHeight: 1.3 }}
        >
          {titulo}
        </MDTypography>
        {children}
      </MDBox>
    </Tooltip>
  );
}

const Cifra = ({ children, color = AZUL, tamano = "1.35rem" }) => (
  <MDTypography component="span" fontWeight="bold" sx={{ color, fontSize: tamano, lineHeight: 1.15 }}>
    {children}
  </MDTypography>
);

const Detalle = ({ children, color = "text.secondary", sx, ...resto }) => (
  <MDTypography display="block" variant="caption" sx={{ color, fontSize: "0.68rem", lineHeight: 1.3, ...sx }} {...resto}>
    {children}
  </MDTypography>
);

// Resumen del usuario logueado en la barra superior de las pantallas: sus gestiones de hoy, su horario del mes, los casos del período de
// gestión del mes y sus puntos. Es solo suyo (el backend toma el usuario del token). Se actualiza solo cada 5 minutos y con el botón de
// actualizar. Si no se puede cargar, no se muestra y la pantalla funciona igual.
function ResumenGestionBanner() {
  const [estado, setEstado] = useState(() => {
    alinearUsuario();
    return { ...guardado };
  });

  useEffect(() => {
    let timer;
    oyentes.add(setEstado);

    // Vuelve a pedir cuando se cumplen los 5 minutos desde el último intento. Con la pestaña escondida no pide: lo hace al volver a verla.
    const programar = (esperaMs) => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!document.hidden) await recargarResumenGestion();
        programar(document.hidden ? CADA_MS : Math.max(1000, CADA_MS - (Date.now() - guardado.ultimoIntentoEl)));
      }, esperaMs);
    };
    const alVolverAVer = async () => {
      if (document.hidden || !hayQueActualizar()) return;
      await recargarResumenGestion();
      programar(CADA_MS);
    };
    document.addEventListener("visibilitychange", alVolverAVer);

    alinearUsuario();
    setEstado({ ...guardado });
    if (hayQueActualizar()) recargarResumenGestion().finally(() => programar(CADA_MS));
    else programar(CADA_MS - (Date.now() - guardado.ultimoIntentoEl));

    return () => {
      oyentes.delete(setEstado);
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", alVolverAVer);
    };
  }, []);

  const { resumen, cargando, actualizadoEl } = estado;
  if (!resumen) return null;

  const { gestionesHoy, gestionesConcretadasHoy, concretadasConfiguradas, casosAsignados, casosGestionados } = resumen;
  const gestionadosPct = porcentaje(casosGestionados, casosAsignados);
  const sinGestionarPct = casosAsignados > 0 ? 100 - gestionadosPct : 0;

  return (
    <MDBox display="flex" alignItems="stretch" flexWrap="wrap" justifyContent="flex-end" gap={1} sx={{ flex: "1 1 auto", minWidth: 0 }}>
      <Cuadro
        titulo={`Gestiones (${diaCorto(resumen.hoy)})`}
        explicacion="Gestiones que cargaste hoy (eventos con estado, sin el evento inicial). Concretadas son las de un tipo de evento marcado como «gestión concretada» en Tipos de Evento."
      >
        {concretadasConfiguradas ? (
          <>
            <Cifra>{numero(gestionesConcretadasHoy)}</Cifra>
            <Detalle color={AZUL}>concretadas · {porcentaje(gestionesConcretadasHoy, gestionesHoy)}%</Detalle>
            <Detalle color="text.primary" fontWeight="medium">{numero(gestionesHoy)} totales</Detalle>
          </>
        ) : (
          <>
            <Cifra>{numero(gestionesHoy)}</Cifra>
            <Detalle color="text.primary" fontWeight="medium">totales</Detalle>
            <Detalle color={NARANJA}>concretadas: sin configurar</Detalle>
          </>
        )}
      </Cuadro>

      <Cuadro
        titulo="Horario promedio"
        explicacion="Promedio, en los días del mes con actividad, de la hora del primer y del último movimiento (el mismo cálculo del Informe de Gestión)."
      >
        {resumen.primerMovimientoPromedio ? (
          <>
            <Cifra tamano="1.05rem">
              {resumen.primerMovimientoPromedio} – {resumen.ultimoMovimientoPromedio ?? "-"}
            </Cifra>
            <Detalle>inicio y fin, en el mes</Detalle>
          </>
        ) : (
          <>
            <Cifra tamano="1.05rem">-</Cifra>
            <Detalle>sin actividad en el mes</Detalle>
          </>
        )}
      </Cuadro>

      <Cuadro
        titulo="Sujetos gestionados (mes)"
        explicacion={`Casos del Período de gestión ${resumen.periodoGestion} que ya tienen al menos una gestión (algún evento que no sea el inicial), sobre los que se te asignaron en ese período.`}
      >
        <Cifra>{numero(casosGestionados)}</Cifra>
        <Detalle component="span" display="inline" sx={{ ml: 0.75 }}>{gestionadosPct}%</Detalle>
      </Cuadro>

      <Cuadro
        titulo="Sujetos asignados (mes)"
        explicacion={`Casos que se te asignaron en el Período de gestión ${resumen.periodoGestion}. «Sin gestionar» son los que no tienen ningún evento salvo el inicial.`}
      >
        <Cifra color={NARANJA}>{numero(casosAsignados)}</Cifra>
        <Detalle>{casosAsignados > 0 ? `${sinGestionarPct}% sin gestionar` : "sin casos en el período"}</Detalle>
      </Cuadro>

      <Cuadro
        titulo="Puntaje"
        explicacion="Puntos de los casos que el sistema te asignó y ya resolviste (los «puntos finalizados» de la Tabla de posiciones): los de este mes y la suma final del mes anterior."
      >
        <MDBox display="flex" alignItems="baseline" gap={0.5}>
          <Cifra color={NARANJA}>{numero(resumen.puntosMes)}</Cifra>
          <Detalle component="span" display="inline">este mes</Detalle>
        </MDBox>
        <MDBox display="flex" alignItems="baseline" gap={0.5}>
          <Cifra color={NARANJA} tamano="0.95rem">{numero(resumen.puntosMesAnterior)}</Cifra>
          <Detalle component="span" display="inline">mes anterior</Detalle>
        </MDBox>
      </Cuadro>

      <Tooltip title={`Actualizar ahora. Última actualización: ${hora(actualizadoEl)}. Se actualiza solo cada 5 minutos.`} arrow placement="bottom">
        {/* El <span> deja que el tooltip funcione aunque el botón esté deshabilitado mientras carga. */}
        <span style={{ alignSelf: "center" }}>
          <IconButton size="small" color="info" onClick={recargarResumenGestion} disabled={cargando} aria-label="Actualizar el resumen">
            {cargando ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </span>
      </Tooltip>
    </MDBox>
  );
}

export default ResumenGestionBanner;
